import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageShell } from '@/components/layout/PageShell';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { StatusDot } from '@/components/ui/StatusDot';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import { Sheet } from '@/components/sheets/Sheet';
import { capture } from '@/modules/heat/signals';
import { useProfiles } from '@/modules/profiles/useProfiles';
import { useProfileBuilder } from '@/modules/auth/useProfileBuilder';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/cn';
import { VENUES, VENUE_KIND_LABEL } from '@/data/venues';

const MapboxSurface = lazy(() =>
  import('@/modules/map/MapboxSurface').then((m) => ({ default: m.MapboxSurface })),
);

type Filter = 'all' | 'live' | 'nearby' | 'venues';

/**
 * Map — Sniffies-style spatial discovery on a real Mapbox-GL surface.
 *
 * The user's actual geolocation drives the "you are here" pin (their own
 * profile photo, ringed in HOTTR pink). Profile pins orbit the user using
 * mapX/mapY projected into a small lat/lng box. Tap a pin → bottom Sheet
 * with profile preview + Open / Send signal CTAs.
 */
export const MapPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeVenueId, setActiveVenueId] = useState<string | null>(null);
  const { profiles } = useProfiles();
  const { draft } = useProfileBuilder();

  useEffect(() => {
    capture('surface_enter', 'map');
  }, []);

  const tiles = useMemo(() => {
    if (filter === 'venues') return [];
    if (filter === 'live') return profiles.filter((p) => p.presence === 'live');
    if (filter === 'nearby') return profiles.filter((p) => parseFloat(p.distance) <= 2);
    return profiles;
  }, [filter, profiles]);

  const venuePins = useMemo(
    () => (filter === 'live' || filter === 'nearby' ? [] : VENUES),
    [filter],
  );

  const active = activeId ? profiles.find((p) => p.id === activeId) ?? null : null;
  const activeVenue = activeVenueId ? VENUES.find((v) => v.id === activeVenueId) ?? null : null;

  const liveCount = profiles.filter((p) => p.presence === 'live').length;
  const nearbyCount = profiles.filter((p) => parseFloat(p.distance) <= 2).length;
  const venueCount = VENUES.length;

  return (
    <PageShell
      rail={false}
      mobileTopBar={
        <>
          {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
              This spacer keeps the live count on the right edge. */}
          <span aria-hidden />
          <div className="flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
            <Icon name="my_location" size={14} />
            <span>{liveCount} live</span>
          </div>
        </>
      }
    >
      {/* HEADLINE + filter pills */}
      <section className="px-5 pb-5 pt-7 md:px-12 md:pb-7 md:pt-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1
            className="font-display font-black uppercase tracking-display"
            style={{
              fontSize: 'clamp(36px, 6vw, 92px)',
              lineHeight: 0.88,
              letterSpacing: '-0.05em',
            }}
          >
            Who&rsquo;s around<span className="text-pink">.</span>
          </h1>
          <div className="flex items-center gap-2">
            <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>
              All · {profiles.length}
            </FilterPill>
            <FilterPill active={filter === 'live'} onClick={() => setFilter('live')}>
              Live · {liveCount}
            </FilterPill>
            <FilterPill active={filter === 'nearby'} onClick={() => setFilter('nearby')}>
              ≤ 2 km · {nearbyCount}
            </FilterPill>
            <FilterPill active={filter === 'venues'} onClick={() => setFilter('venues')}>
              Venues · {venueCount}
            </FilterPill>
          </div>
        </div>
      </section>

      {/* MAP CANVAS — real Mapbox-GL surface */}
      <section className="relative">
        <div
          className={cn(
            'relative w-full overflow-hidden bg-ink',
            'h-[68dvh] md:h-[72dvh]',
            'md:mx-12 md:my-6 md:border md:border-white/[0.07]',
          )}
        >
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center bg-ink">
                <span className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
                  Loading map…
                </span>
              </div>
            }
          >
            <MapboxSurface
              tiles={tiles}
              activeId={activeId}
              onPickPin={(id) => {
                setActiveId(id);
                setActiveVenueId(null);
                capture('profile_view', 'map', id);
              }}
              userPhotoUrl={draft.photos[0] ?? null}
              userInitial={(draft.handle || draft.displayName || 'H').slice(0, 1)}
              venues={venuePins}
              activeVenueId={activeVenueId}
              onPickVenue={(id) => {
                setActiveVenueId(id);
                setActiveId(null);
                capture('surface_enter', 'map', id);
              }}
            />
          </Suspense>
        </div>

        <p className="px-5 py-6 text-center font-display text-[9px] font-bold uppercase tracking-widest text-text-muted md:px-12 md:py-8">
          Live within the hour<span className="text-pink">.</span> Locations are coarse on purpose<span className="text-pink">.</span>
        </p>
      </section>

      {/* PROFILE PREVIEW SHEET */}
      <Sheet
        open={!!active}
        onClose={() => setActiveId(null)}
        eyebrow={
          active
            ? `${active.distance} · ${active.location}${active.age ? ` · ${active.age}` : ''}`
            : undefined
        }
        title={
          active ? (
            <>
              @{active.handle}<span className="text-pink">.</span>
            </>
          ) : null
        }
        footer={
          active && (
            <div className="flex items-center gap-3">
              <NeonButton
                variant="outline"
                size="md"
                onClick={() => setActiveId(null)}
              >
                Close
              </NeonButton>
              <NeonButton
                size="md"
                fullWidth
                trailing={<Icon name="arrow_forward" size={14} />}
                onClick={() => {
                  if (active) navigate(ROUTES.profile(active.handle));
                }}
              >
                Open profile
              </NeonButton>
            </div>
          )
        }
      >
        {active && (
          <div className="space-y-5">
            <div className="relative h-44 w-full overflow-hidden border border-white/[0.07]">
              <EditorialImage
                scene={active.scene}
                seed={active.sceneSeed}
                photoUrl={active.photos[0]}
                aspect="aspect-auto"
                className="h-full"
              />
              {active.presence === 'live' && (
                <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 border border-pink/40 bg-ink/85 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest text-pink backdrop-blur">
                  <StatusDot status="live" size="h-1.5 w-1.5" />
                  live
                </span>
              )}
            </div>

            <p className="text-sm text-text-secondary">{active.mood}</p>

            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              {active.height}
              <span className="mx-1.5 text-pink">·</span>
              {active.body}
              <span className="mx-1.5 text-pink">·</span>
              {active.position}
              <span className="mx-1.5 text-pink">·</span>
              {active.status}
            </p>

            <div className="flex flex-wrap gap-2">
              {active.lookingFor.map((tag) => (
                <span
                  key={tag}
                  className="border border-pink/30 bg-pink/[0.06] px-2 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-pink"
                >
                  {tag}
                </span>
              ))}
            </div>

            <Link
              to={ROUTES.profile(active.handle)}
              className="block font-display text-[10px] font-bold uppercase tracking-widest text-text-muted hover:text-pink"
              onClick={() => setActiveId(null)}
            >
              See full profile →
            </Link>
          </div>
        )}
      </Sheet>

      {/* VENUE PREVIEW SHEET */}
      <Sheet
        open={!!activeVenue}
        onClose={() => setActiveVenueId(null)}
        eyebrow={
          activeVenue
            ? `${VENUE_KIND_LABEL[activeVenue.kind]} · ${activeVenue.city} · ${activeVenue.country}`
            : undefined
        }
        title={
          activeVenue ? (
            <>
              {activeVenue.name}<span className="text-pink">.</span>
            </>
          ) : null
        }
        footer={
          activeVenue && (
            <div className="flex items-center gap-3">
              <NeonButton
                variant="outline"
                size="md"
                onClick={() => setActiveVenueId(null)}
              >
                Close
              </NeonButton>
              <NeonButton
                size="md"
                fullWidth
                trailing={<Icon name="north_east" size={14} />}
                onClick={() => {
                  if (!activeVenue) return;
                  const q = encodeURIComponent(`${activeVenue.name} ${activeVenue.city}`);
                  window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
                }}
              >
                Directions
              </NeonButton>
            </div>
          )
        }
      >
        {activeVenue && (
          <div className="space-y-5">
            <p className="text-sm text-text-secondary">{activeVenue.blurb}</p>

            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              {activeVenue.city}
              <span className="mx-1.5 text-pink">·</span>
              {VENUE_KIND_LABEL[activeVenue.kind]}
              <span className="mx-1.5 text-pink">·</span>
              {activeVenue.lat.toFixed(3)}, {activeVenue.lng.toFixed(3)}
            </p>
          </div>
        )}
      </Sheet>
    </PageShell>
  );
};

/* ============================================================
   FILTER PILL
   ============================================================ */

const FilterPill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={cn(
      'border px-3 py-1.5 font-display text-[10px] font-bold uppercase tracking-widest',
      'transition-[border-color,color,background] duration-fast ease-signal',
      active
        ? 'border-pink bg-pink/10 text-pink shadow-glow-soft'
        : 'border-white/15 text-text-secondary hover:border-pink/40 hover:text-text-primary',
    )}
  >
    {children}
  </button>
);

